import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import { ApiError, createBuilding, deleteBuilding, getBuildings, updateBuilding } from '@/lib/api';
import type { Building, CreateBuildingInput, Facility, UpdateBuildingInput } from '@/lib/types';
import { Building2, Edit2, Plus, ToggleLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BUILDING_TYPES = [
  { value: 'boarding', label: 'Boarding' },
  { value: 'daycare', label: 'Daycare' },
  { value: 'grooming', label: 'Grooming' },
  { value: 'cattery', label: 'Cattery' },
  { value: 'isolation', label: 'Isolation' },
  { value: 'mixed', label: 'Mixed use' },
  { value: 'storage', label: 'Storage' },
  { value: 'other', label: 'Other' },
] as const;

type BuildingTypeValue = (typeof BUILDING_TYPES)[number]['value'];

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

interface BuildingFormState {
  facilityId: string;
  name: string;
  buildingType: BuildingTypeValue;
  description: string;
  notes: string;
  position: string;
}

function defaultForm(facilityId: string): BuildingFormState {
  return {
    facilityId,
    name: '',
    buildingType: 'boarding',
    description: '',
    notes: '',
    position: '0',
  };
}

function formFromBuilding(b: Building): BuildingFormState {
  return {
    facilityId: b.facilityId,
    name: b.name,
    buildingType: b.buildingType as BuildingTypeValue,
    description: b.description ?? '',
    notes: b.notes ?? '',
    position: String(b.position),
  };
}

interface BuildingFormProps {
  facilities: Facility[];
  initial: BuildingFormState;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (form: BuildingFormState) => void;
  onCancel: () => void;
  showFacilityPicker?: boolean;
}

function BuildingForm({
  facilities,
  initial,
  busy,
  error,
  submitLabel,
  onSubmit,
  onCancel,
  showFacilityPicker = false,
}: BuildingFormProps) {
  const [form, setForm] = useState<BuildingFormState>(initial);
  const isValid = form.name.trim().length > 0 && form.facilityId.length > 0;

  return (
    <div className="space-y-4">
      {showFacilityPicker && (
        <div>
          <label
            htmlFor="bld-facility"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Facility
          </label>
          <select
            id="bld-facility"
            value={form.facilityId}
            onChange={(e) => setForm((p) => ({ ...p, facilityId: e.target.value }))}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label
          htmlFor="bld-name"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Name
        </label>
        <input
          id="bld-name"
          type="text"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          placeholder="Main Building"
          maxLength={80}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      <div>
        <label
          htmlFor="bld-type"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Building type
        </label>
        <select
          id="bld-type"
          value={form.buildingType}
          onChange={(e) =>
            setForm((p) => ({ ...p, buildingType: e.target.value as BuildingTypeValue }))
          }
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          {BUILDING_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="bld-desc"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Description <span className="text-muted-foreground/50">(optional)</span>
        </label>
        <input
          id="bld-desc"
          type="text"
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          placeholder="E.g. 8-run dog boarding wing"
          maxLength={200}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      <div>
        <label
          htmlFor="bld-notes"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Internal notes <span className="text-muted-foreground/50">(optional)</span>
        </label>
        <textarea
          id="bld-notes"
          value={form.notes}
          onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          rows={2}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      <div>
        <label
          htmlFor="bld-position"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Display order
        </label>
        <input
          id="bld-position"
          type="number"
          value={form.position}
          onChange={(e) => setForm((p) => ({ ...p, position: e.target.value }))}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!isValid || busy}
          onClick={() => onSubmit(form)}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

interface AddModalProps {
  facilities: Facility[];
  defaultFacilityId: string;
  onClose: () => void;
  onCreated: () => void;
}

function AddModal({ facilities, defaultFacilityId, onClose, onCreated }: AddModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(form: BuildingFormState) {
    setBusy(true);
    setError(null);
    const body: CreateBuildingInput = {
      facilityId: form.facilityId,
      name: form.name.trim(),
      buildingType: form.buildingType,
      description: form.description.trim() || null,
      notes: form.notes.trim() || null,
      position: Number.isNaN(Number(form.position)) ? 0 : Number(form.position),
    };
    try {
      await createBuilding(body);
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label="Add building">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Add building</h2>
        <BuildingForm
          facilities={facilities}
          initial={defaultForm(defaultFacilityId)}
          busy={busy}
          error={error}
          submitLabel="Add building"
          onSubmit={(f) => void handleSubmit(f)}
          onCancel={onClose}
          showFacilityPicker={facilities.length > 1}
        />
      </div>
    </Modal>
  );
}

interface EditModalProps {
  building: Building;
  facilities: Facility[];
  onClose: () => void;
  onUpdated: () => void;
}

function EditModal({ building, facilities, onClose, onUpdated }: EditModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(form: BuildingFormState) {
    setBusy(true);
    setError(null);
    const patch: UpdateBuildingInput = {
      name: form.name.trim(),
      buildingType: form.buildingType,
      description: form.description.trim() || null,
      notes: form.notes.trim() || null,
      position: Number.isNaN(Number(form.position)) ? 0 : Number(form.position),
    };
    try {
      await updateBuilding(building.id, patch);
      onUpdated();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Edit building</h2>
        <BuildingForm
          facilities={facilities}
          initial={formFromBuilding(building)}
          busy={busy}
          error={error}
          submitLabel="Save changes"
          onSubmit={(f) => void handleSubmit(f)}
          onCancel={onClose}
        />
      </div>
    </Modal>
  );
}

interface DeactivateModalProps {
  building: Building;
  onCancel: () => void;
  onConfirmed: () => void;
}

function DeactivateModal({ building, onCancel, onConfirmed }: DeactivateModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await deleteBuilding(building.id);
      onConfirmed();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Deactivate "{building.name}"?</h2>
        <p className="text-sm text-muted-foreground">
          This building will be marked inactive and hidden from new run assignments. Runs already
          assigned to it are preserved.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal state
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'edit'; building: Building }
  | { kind: 'deactivate'; building: Building };

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function StatusPill({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
      Inactive
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function BuildingsSettings() {
  const { state } = useAuthContext();
  const facilities = state.me?.facilitiesInScope ?? [];
  const defaultFacilityId = state.me?.activeFacility?.id ?? facilities[0]?.id ?? '';

  const [filterFacilityId, setFilterFacilityId] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  useEffect(() => {
    if (defaultFacilityId && !filterFacilityId) {
      setFilterFacilityId(defaultFacilityId);
    }
  }, [defaultFacilityId, filterFacilityId]);

  const refresh = useCallback(async () => {
    if (!filterFacilityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setPermissionDenied(false);
    try {
      const rows = await getBuildings(filterFacilityId, true);
      setBuildings(rows);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) setPermissionDenied(true);
    } finally {
      setLoading(false);
    }
  }, [filterFacilityId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const displayed = showInactive ? buildings : buildings.filter((b) => b.isActive === 1);

  const facilityName = (id: string) => facilities.find((f) => f.id === id)?.name ?? id;

  const buildingTypeLabel = (type: string) =>
    BUILDING_TYPES.find((t) => t.value === type)?.label ?? type;

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage buildings
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Buildings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Organise your runs by physical building or wing. Runs can be assigned to a building in
            the Runs settings.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'add' })}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          Add building
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-4">
        {facilities.length > 1 && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="filter-facility"
              className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Facility
            </label>
            <select
              id="filter-facility"
              value={filterFacilityId}
              onChange={(e) => setFilterFacilityId(e.target.value)}
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-border"
          />
          Show inactive
        </label>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : displayed.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Building2 className="mx-auto mb-3 size-8 text-muted-foreground/40" strokeWidth={1.25} />
          <p className="text-sm font-medium text-foreground">No buildings configured yet.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Buildings are optional — use them to group runs by physical location on large sites.
          </p>
          <button
            type="button"
            onClick={() => setModal({ kind: 'add' })}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            Add building
          </button>
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] gap-4 border-b border-border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            <span>Name</span>
            {facilities.length > 1 && <span>Facility</span>}
            <span>Type</span>
            <span>Description</span>
            <span>Status</span>
            <span />
          </div>
          <div className="divide-y divide-border">
            {displayed.map((building) => (
              <div
                key={building.id}
                className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] items-center gap-4 px-6 py-3.5 text-sm"
              >
                <span className="font-medium">{building.name}</span>
                {facilities.length > 1 && (
                  <span className="text-muted-foreground">{facilityName(building.facilityId)}</span>
                )}
                <span className="text-muted-foreground capitalize">
                  {buildingTypeLabel(building.buildingType)}
                </span>
                <span className="truncate text-muted-foreground">
                  {building.description ?? <span className="text-muted-foreground/40">—</span>}
                </span>
                <StatusPill active={building.isActive === 1} />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setModal({ kind: 'edit', building })}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground press"
                    title="Edit building"
                  >
                    <Edit2 className="size-3.5" strokeWidth={1.75} />
                    Edit
                  </button>
                  {building.isActive === 1 && (
                    <button
                      type="button"
                      onClick={() => setModal({ kind: 'deactivate', building })}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive press"
                      title="Deactivate building"
                    >
                      <ToggleLeft className="size-3.5" strokeWidth={1.75} />
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {modal.kind === 'add' && (
        <AddModal
          facilities={facilities}
          defaultFacilityId={filterFacilityId || defaultFacilityId}
          onClose={() => setModal({ kind: 'none' })}
          onCreated={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
      {modal.kind === 'edit' && (
        <EditModal
          building={modal.building}
          facilities={facilities}
          onClose={() => setModal({ kind: 'none' })}
          onUpdated={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
      {modal.kind === 'deactivate' && (
        <DeactivateModal
          building={modal.building}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
    </div>
  );
}
